import { ref, toValue, watch, type MaybeRefOrGetter } from 'vue'
import type { AlgoliaLocation } from '~/composables/types/algolia'
import { useAlgoliaIndexNames } from '~/composables/algolia/useAlgoliaIndexNames'
import { localitySlugForAlgoliaOptionalFilter } from '~/utils/localitySlugMatch'
import { buildAlgoliaAnalyticsTags } from '~/utils/algoliaHelpers'
import { applyAlgoliaSearchAnalyticsGuard } from '~/utils/algoliaSearchAnalytics'

export const useAlgoliaLocalities = async (
  initialLocationSlug: MaybeRefOrGetter<string> = '',
  immediate = true
) => {
  const searchQuery = ref('')
  const locationSlug = ref(toValue(initialLocationSlug))

  watch(
    () => toValue(initialLocationSlug),
    (v) => {
      locationSlug.value = v ?? ''
    }
  )

  const {
    public: { appEnv },
  } = useRuntimeConfig()
  const defaultReqOpts = {
    similarQuery: '',
    clickAnalytics: true,
    hitsPerPage: 20,
    analyticsTags: buildAlgoliaAnalyticsTags(appEnv),
  }
  const { locations: locationsIndex } = useAlgoliaIndexNames()

  const { result: localitiesAlgolia, search } =
    useAlgoliaIndexSearch<AlgoliaLocation>(locationsIndex)

  const { execute } = await useAsyncData(
    locationsIndex,
    () => {
      const slugOpt = localitySlugForAlgoliaOptionalFilter(locationSlug.value)
      return search({
        query: searchQuery.value,
        requestOptions: applyAlgoliaSearchAnalyticsGuard(
          {
            ...defaultReqOpts,
            optionalFilters: [
              // give priority to localities if no slug is passed
              ...(!slugOpt ? ['type:locality'] : []),
              ...(slugOpt ? [`slug:${slugOpt}`] : []),
            ],
            // search around user's location if no slug is passed
            aroundLatLngViaIP:
              !slugOpt && import.meta.client && !searchQuery.value,
          },
          searchQuery.value
        ),
      })
    },
    {
      immediate,
      deep: true,
      watch: [locationSlug, searchQuery],
    }
  )

  async function searchLocalities(query: string) {
    searchQuery.value = query
    return await execute()
  }

  async function searchLocalitiesWithOptionalLocationFilter(slug: string) {
    locationSlug.value = slug
    return await execute()
  }

  return {
    localitiesAlgolia,
    setLocationSlug: (slugVal: string) => {
      locationSlug.value = slugVal
    },
    searchLocalities,
    searchLocalitiesWithOptionalLocationFilter,
  }
}
